"use client"
import {useMonitoring} from "@/components/monitoring";

import React, {useState, useEffect, useRef, useMemo} from "react";
import throttle from 'lodash.throttle';
import {Button} from "@heroui/button"

import {Input} from "@heroui/input";
import {useModal} from "@/components/DownloaderApp/Modal";
import {useNetworkCheck} from "@/components/DownloaderApp/Network";
import {Link} from "@heroui/link";
import {setupCommunication} from "@/utils/setup-communication";
import {ChromeExtensionPopup} from "@/components/DownloaderApp/ChromeExtensionPopup";

import {
    getPlatformAndContentTypeByUrl,
    isKickStreamUrl,
} from "@/components/DownloaderApp/platform-detection";
import {
    getKickChannelInfo,
    getKickClip, getKickClips,
    getKickVod, getKickVod2,
} from "@/components/DownloaderApp/kick";
import {getTwitchClip, getTwitchVod} from "@/components/DownloaderApp/twitch";
import {
    fetchRumbleVideoDetails,
    buildPlaylistUrl as buildRumblePlaylistUrl
} from "@/components/DownloaderApp/rumble-fetch";
import {
    calculateVideoSize2,
    checkStorageUsage, CHROME_EXTENSION_URL,
    errorMessageOf, fetchText, getExtensionVersion, getQueryParam, hlsParse,
    isDev, promptTabClose, showConfetti,
    slugify, storeSavedUrl, triggerDownload, tryCatch, saveMp3Download, discardMp3File,
    type FilePromptResult,
    writeFilePrompt, toMp3
} from "@/components/DownloaderApp/utils";
import {QuotaExceededModal} from "@/components/DownloaderApp/QuotaExceededModal";
import {DonateWithPaypal} from "@/components/DownloaderApp/DonateWithPaypal";
import {DownloadSaveInfo, Source, Video} from "@/components/DownloaderApp/types";
import type {DownloaderAppProps} from "@/components/DownloaderApp/types";
import type {RumblePlaylistVideo} from "@/app/api/handlers/get-rumble-playlist";
import {logger} from "@/components/DownloaderApp/logger";
import {ChooseQualityPopup} from "@/components/DownloaderApp/ChooseQualityPopup";
import {RumbleChooseQualityPopup} from "@/components/DownloaderApp/RumbleChooseQualityPopup";
import {
    downloadPlaylistChunks,
    reportInterruptedPlaylistDownload
} from "@/components/DownloaderApp/downloadPlaylistChunks";
import {ChromeExtensionCloudflareDodgePopup} from "@/components/DownloaderApp/ChromeExtensionCloudflareDodgePopup";
import {BaselineError} from "@/components/icons";
import {getVideoDetails} from "@/components/DownloaderApp/getVideoDetails";
import {getDownloadConfig} from "@/components/DownloaderApp/getDownloadConfig";
import {FinalizingMessage, ProgressBar, randomInRange} from "@/components/DownloaderApp/ProgressBar";
import {ClipsPopup} from "@/components/DownloaderApp/ClipsPopup";
import {showUpsellModal, UpsellPopup} from "@/components/DownloaderApp/UpsellPopup";
import {isInNewUpsellExperiment} from "@/utils/is-in-new-upsell-experiment";
import {showTwitchUpsellModal} from "@/components/DownloaderApp/TwitchUpsellPopup";
import {getLicenseKey, PremiumManager, usePremium} from "@/components/DownloaderApp/usePremium";
import SubOnlyContentAvailable from "@/components/DownloaderApp/SubOnlyContentAvailable";
import {DownloadIcon, QualityIcon} from "@/components/DownloaderApp/icons";
import {Bold} from "@/components/DownloaderApp/Bold";
import {DownloadSavedGuide} from "@/components/DownloaderApp/DownloadSavedGuide";
import {HorizontalChannelVideos} from "@/components/ChannelVideos/HorizontalChannelVideos";
import type {Video2} from "@/utils/kick-video";
import {
    isArrayBufferAllocationError,
    isMissingDownloadDestinationError,
    isMp3ConversionError,
    isPlaylistFetchError,
    isStreamSaverError,
    isWriteAbortedError
} from "@/components/DownloaderApp/download-errors";

export function parseFfmpegOutput(output: string) {
    const timeMatch = output.match(/time=(\d{2}):(\d{2}):(\d{2})\.(\d{2})/);
    if (!timeMatch) {
        return null
    }
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const seconds = parseInt(timeMatch[3], 10);
    const centiseconds = parseInt(timeMatch[4], 10);

    return hours * 3600 + minutes * 60 + seconds + centiseconds / 100;
}

let monitoringContext = {}

// These describe the page, not a single download, and their effects only re-run when
// the url or query params actually change. Everything else is per-download and would
// otherwise leak into the next download in the same page session.
const PERSISTENT_MONITORING_KEYS = ["queryParamUrl", "source", "url"];
const TEST_VIDEOS = [
    ["Kick"],
    ["https://kick.com/wariatp2w/videos/2b4e82a4-6b51-4b7a-9326-acf099b3ad34", "KICK VOD OK"],
    ["https://kick.com/mokrysuchar/videos/1741d620-567e-413d-8495-309f4b341c0c", "KICK VOD failed"],
    ["https://kick.com/video/013909fa-d9a5-4bce-84cc-1550c5380fe6", "KICK VOD 404"],
    ["https://kick.com/wariatp2w/clips/clip_01J46JKSDZR8R348BN4BD99GEE", "kick clip"],
    ["https://kick.com/toksics/videos/3d749cea-efff-4429-a716-be045be973f3", "kick 403"],
    ["https://kick.com/cabrzy/clips/clip_01GX75RDRR155RR1K2WPJ9G1NV", "kick clip mp4 xd"],
    ["twitch"],
    ["https://www.twitch.tv/wandall72/clip/MoistTransparentMarjoramYee-6sA6PTjuJofH6RYj", "twitch clip"],
    ["https://www.twitch.tv/videos/1876507211", "twitch VOD"],
    ["https://www.twitch.tv/videos/2365524149", "twitch VOD unavailable"],
    ["https://www.twitch.tv/mrnovatoo/video/2495795160", "different pathname"],
    ["non platform"],
    ["https://bitdash-a.akamaihd.net/content/sintel/hls/playlist.m3u8", "m3u8"],
    ["https://stream.kick.com/ivs/v1/196233775518/RU3EzynKSP3E/2025/4/4/17/11/HEFCwJbS21hu/media/hls/master.m3u8", "m3u8"],
    ["https://clips.kick.com/clips/8a/clip_01JQEVP3T5QWVGFTTBVGHE97Q7/playlist.m3u8", "m3u8"],
    ["https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8", "m3u8"],
]

const EMOJI_OPTIONS = ["🎉", "👏", "🎬", "🚀", "🔥", "🥳", "🥹", "😍", "👌", "💪", "😇","🙌"];
const EMOJI = EMOJI_OPTIONS[randomInRange(0, EMOJI_OPTIONS.length - 1)];

const MP3_OUT_OF_MEMORY_MESSAGE = <>
    <b>Not enough memory to convert this video to MP3.</b>
    <p>
        Your browser could not allocate enough memory for the video. Try converting a shorter section,
        closing other tabs, or using a device with more memory.
    </p>
</>;

const MP3_CONVERSION_STAGE_MESSAGES: Record<string, string> = {
    "read-stream": "We couldn't read the downloaded video before converting it.",
    "load-ffmpeg": "The audio converter couldn't be loaded. Check your connection or any blockers, then try again.",
    "write-input": "The video couldn't be handed over to the audio converter.",
    "convert": "The audio converter couldn't process this video.",
    "read-output": "The converted audio file couldn't be read.",
};

const mp3ConversionMessage = (err: unknown) => {    if (!isMp3ConversionError(err)) return null;
    if (isArrayBufferAllocationError(err)) return MP3_OUT_OF_MEMORY_MESSAGE;

    const isNoAudioIssue = err?.logTail.find(logLine => logLine.includes('does not contain any stream'));
    return <>
        <b>Converting this video to MP3 failed.</b>
        <p>{isNoAudioIssue ? `The video doesn't seem to have an audio 🤯` : (MP3_CONVERSION_STAGE_MESSAGES[err.stage] || "Something went wrong during the conversion.")}</p>
    </>;
};

const MISSING_DESTINATION_ERROR_MESSAGE = <>
    <b>The selected download location is no longer available.</b>
    <p>
        The file or its folder may have been moved, deleted, or disconnected while the video was
        downloading. Start the download again, choose an available folder, and do not move or delete
        the file until it finishes.
    </p>
</>;

const WRITE_ABORTED_ERROR_MESSAGE = <>
    <b>Your browser stopped writing the file before the download finished.</b>
    <p>
        The download was interrupted or cancelled. Start the download again and keep this tab open
        until it finishes. Any partial file may be incomplete.
    </p>
</>;

const QUOTA_EXCEEDED_ERROR_TEXT = "The operation failed because it would cause the application to exceed its storage quota.";
const QUOTA_EXCEEDED_ERROR_MESSAGE = <>
    <b>The operation failed because it would cause the application to exceed its storage quota.</b>
</>;

const SAVE_PERMISSION_ERROR_TEXT = "The request is not allowed by the user agent or the platform in the current context";
const SAVE_PERMISSION_ERROR_MESSAGE = <>
    <b>We can't save a file on your device, because of lack of permissions.</b>
    <p>Check your settings:<br/>
        Chrome Settings &gt; Privacy and Security &gt; Site Settings &gt; Permissions &gt; File editing
        (under
        additional permissions)
    </p>
</>;

// Same precedence as downloadAndSave: later checks win.
const saveErrorMessage = (err: unknown) => {
    const errText = errorMessageOf(err);
    if (errText.includes(SAVE_PERMISSION_ERROR_TEXT)) return SAVE_PERMISSION_ERROR_MESSAGE;
    if (errText.includes(QUOTA_EXCEEDED_ERROR_TEXT)) return QUOTA_EXCEEDED_ERROR_MESSAGE;
    if (isWriteAbortedError(err)) return WRITE_ABORTED_ERROR_MESSAGE;
    if (isMissingDownloadDestinationError(err)) return MISSING_DESTINATION_ERROR_MESSAGE;
    return null;
};

const mp3ErrorContext = (err: unknown) => {
    if (!isMp3ConversionError(err)) return {};
    return {
        mp3_stage: err.stage,
        mp3_exit_code: err.exitCode,
        mp3_ffmpeg_log: err.logTail.join("\n"),
    };
};

function parseSource(queryParamUrl, queryParamSource): Source | null {
    if (queryParamSource === "c") {
        return "clips"
    }
    if (queryParamUrl) {
        return "extension"
    }
    return null
}

export function DownloaderApp({Platform = "kick", enforceExtenstion}: DownloaderAppProps = {}) {
    const {show} = useModal()
    const {track, updateUser} = useMonitoring()
    const {isOnline, timestamp} = useNetworkCheck()
    const userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent;

    let queryParamUrl = '';
    let queryParamSource: Source = null;
    let queryParamForceDownload = false;
    let queryParamDuration: string | null = null;
    let queryParamPrivate = false;
    try {
        queryParamUrl = getQueryParam(window.location.href, "download");
        queryParamSource = getQueryParam(window.location.href, "s");
        queryParamForceDownload = !!getQueryParam(window.location.href, "forceDownload");
        queryParamDuration = getQueryParam(window.location.href, "duration") || null;
        queryParamPrivate = getQueryParam(window.location.href, "private") !== null;
    } catch (err) {
    }

    const {hasPremium, details, refresh} = usePremium();

    const [url, setUrl] = useState(
        queryParamUrl || (isDev ? "https://www.twitch.tv/videos/1876507211" : "")
    );
    const [extension, setExtension] = useState(null);
    const [emitter, setEmitter] = useState(null);
    const [state, setState] = useState<"default" | "preparing" | "downloading" | "saved" | "error">("default");
    const [error, setError] = useState<React.ReactNode>("");
    const [video, setVideo] = useState<(Video & { isLive: boolean }) | null>(null);
    const [progress, setProgress] = useState(null);
    const [estimated, setEstimated] = useState(0);
    const [downloadSaveInfo, setDownloadSaveInfo] = useState<DownloadSaveInfo | null>(null);
    const [playlistVideos, setPlaylistVideos] = useState<RumblePlaylistVideo[] | null>(null);
    const [playlistLoading, setPlaylistLoading] = useState(false);
    const [clickedPlaylistItems, setClickedPlaylistItems] = useState<Set<string>>(new Set());
    const forceDownloadTriggered = useRef(false);

    useEffect(() => {
        reportInterruptedPlaylistDownload();
    }, []);

    async function promptChromeExtensionModal() {
        await show({
            component: ChromeExtensionPopup
        })
        setError(<>To download <b>Twitch.tv</b> videos, install <Link target="_blank"
                                                                      className={"text-black underline underline-offset-4"}
                                                                      href={CHROME_EXTENSION_URL}>the
            chrome extension</Link></>)
    }


    useEffect(() => {
        logger.log({isOnline, timestamp})
        if (isOnline && timestamp > 0) {
            const period = (Date.now() - timestamp) / 1000
            // track("Network Issues Resolved", {period})
        }
    }, [isOnline, timestamp]);


    useEffect(() => {
        try {
            window.posthog?.stopSessionRecording();
        }catch(err) {

        }
        // showUpsellModal(show)
        if (isDev) {
            // setTimeout(() => {
            //     downloadClicked(true)
            // })
        }
        return;
        const channelname = "wariatp2w";
        getKickClips(channelname).then(async a => {
            const {accepted, data} = await show({
                component: ClipsPopup,
                data: {
                    scrollBehavior: "inside",
                    className: "h-4/5",
                    size: '4xl',
                    clips: a.clips,
                    channelName: channelname,
                },
            })
        })
        // downloadClicked(true)
    }, []);
    function prefetchThumbnail(video: Video2 | undefined) {
        if(!video) return;
        if(video.status === 'public') return;
        if(getLicenseKey()) return;

        const url = video.thumbnailUrl;
        if(!url) return;

        function prefetchImage(url: string) {
            const link = document.createElement("link");
            link.rel = "prefetch";
            link.as = "image";
            link.href = url;
            document.head.appendChild(link);
        }
        prefetchImage(url)
    }

    useEffect(() => {
        const contentDetails = getPlatformAndContentTypeByUrl(url);
        if (!contentDetails) return;

        const {platform, type, id, channel} = contentDetails;
        let details = null;
        if (isKickStreamUrl(url)) {
            getKickVod2(url, null).then(vodResponse => {
                prefetchThumbnail(vodResponse.json?.video2);
            }).catch(error => {
                console.error('[get-kick-video-by-stream] error', error);
            });
        }
        if (platform === 'kick') {
            if (type === 'vod') {
                // details = getKickVod(id).then(vod => fetchText(vod.source));

                (async () => {
                    const vodResponse = await getKickVod2(url, channel ? await getKickChannelInfo(channel) : null);
                    const video = vodResponse.json?.video2;
                    prefetchThumbnail(video);
                    if (!video?.source) return;
                    await Promise.all([
                        fetchText(video.source),
                        fetchText(video.source.replace('master.m3u8', '1080p60/playlist.m3u8')),
                    ]);
                })().catch(error => {
                    console.error('[get-kick-video2] Failed to prefetch Kick VOD', error);
                });
            }
            if (type === 'clip') {
                details = getKickClip(id).then(clip => fetchText(clip.clip_url))
            }
            if (type === 'clips') {
            }
        }
        if (platform === 'twitch') {
            if (type === 'vod') {
                details = getTwitchVod(id)
            }
            if (type === 'clip') {
                details = getTwitchClip(url)
            }
        }
        (async () => {
            logger.log({...contentDetails, details: await details})
        })()
    }, [url])

    useEffect(() => {
        if (state !== "downloading") return;

        window.addEventListener("beforeunload", promptTabClose);
        return () => window.removeEventListener("beforeunload", promptTabClose);
    }, [state])


    useEffect(() => {
        // try {
        //     if (state === "downloading") {
        //         window.posthog?.stopSessionRecording();
        //     } else {
        //         window.posthog?.startSessionRecording();
        //     }
        // } catch (err) {
        // }
        if (state === "saved") {
            selectInput();
            try {
                const state = storeSavedUrl(url);
                if(hasPremium) {
                    updateUser({
                        total_saved: (state.saved || []).length,
                        saved: (state.saved || [])
                    })
                }
            } catch (err) {
                track("Local Storage Error", {
                    message: err?.message || "unknown"
                })
            }
        }
    }, [state])

    useEffect(() => {
        selectInput()
        const {sendMessage} = setupCommunication({
            me: 'kick-video.download:webapp',
            them: 'kick-video.download:extension',
            expose: {
                'ping': () => 'pong from webapp'
            }
        })
        window.sendMessage = sendMessage;
        sendMessage('get-version').then(extension => {
            // track("Installed Extension Version", {
            //     extensionVersion: extension.version,
            //     extensionManifest: extension,
            // })
            setExtension(extension);
            logger.log(`App version: ${extension.version}`);
        })
    }, []);

    useEffect(() => {
        const source = parseSource(queryParamUrl, queryParamSource)
        updateMonitoringContext({queryParamUrl, source})
    }, [queryParamUrl, queryParamSource])

    useEffect(() => {
        if (url.startsWith("kick.com/") || url.startsWith("rumble.com/")) {
            setUrl(`https://${url}`)
        } else if (url.startsWith("http://kick.com") || url.startsWith("http://rumble.com")) {
            setUrl(url.replace('http://', 'https://'))
        } else {
            updateMonitoringContext({url})
        }
    }, [url]);

    useEffect(() => {
        if (!queryParamForceDownload || !url || forceDownloadTriggered.current) return;
        forceDownloadTriggered.current = true;
        downloadClicked(true);
        const cleaned = new URL(window.location.href);
        cleaned.searchParams.delete("forceDownload");
        window.history.replaceState({}, "", cleaned.toString());
    }, [url]);

    useEffect(() => {
        if (!'showSaveFilePicker' in window) {
            setError("Your browser is not supported. Try latest Chrome, Edge or Opera on desktop. Sorry!");
            track("Error", {
                ...monitoringContext,
                message: "Your browser is not supported. Try latest Chrome, Edge or Opera on desktop. Sorry!"
            });
            setState("error");
        }
    }, []);

    useEffect(() => {
        if (state === 'default' || state === 'preparing') return;
        track(`State Changed`, {...monitoringContext, state})
    }, [state])


    const trackProcessSegmentFailed = throttle((data) => {
        // track('processSegment Failed', {
        //     ...monitoringContext,
        //     ...data
        // });
    }, 1000 * 10);

    async function askForDownloadPreferences(masterPlaylist, hqPlaylist, video) {

        function getDefaultTrimById(id) {
            try {
                const key = sessionStorage[slugify(id)];
                if (!key) return null;
                return JSON.parse(key);
            } catch (err) {
                console.log(err)
                return null
            }
        }

        const queryParamDurationSec = queryParamDuration ? Number(queryParamDuration) : null;
        const fromSec = video.fromSec ?? null;
        const secTrim = (fromSec != null || queryParamDurationSec != null)
            ? [fromSec ?? 0, queryParamDurationSec != null ? (fromSec ?? 0) + queryParamDurationSec : null]
            : null;

        const {accepted, data} = await show({
            component: ChooseQualityPopup,
            data: {
                size: "3xl",
                masterPlaylist,
                hqPlaylist,
                details,
                video,
                enforceExtenstion,
                trim: getDefaultTrimById(masterPlaylist._url),
                secTrim,
                title: 'Choose quality',
            },
        })

        if (accepted) {
            // track("Quality chosen", {accepted, ...(data || {})})
            return data;
        }
        return null;
    }

    function selectInput() {
        setTimeout(() => {
            inputRef?.current?.select?.()
        }, 1)
    }

    function resetForm() {
        setState("default")
        setError("")
        setEstimated(0)
        setVideo(null);
        setProgress(null);
        setDownloadSaveInfo(null);
        selectInput();
    }

    async function downloadClicked(chooseQuality = false) {
        resetMonitoringContext();
        resetForm();
        // track("Download Clicked", {...monitoringContext, chooseQuality});
        setState("preparing");
        let video: Video;
        let isSavingRumbleMp3 = false;
        try {
            //=============== get video details first

            const contentType = getPlatformAndContentTypeByUrl(url);

            if (contentType === null) {
                throw new Error(`${url} is not valid url`)
            }

            if (enforceExtenstion === "vtt" && contentType.type !== "vod" &&
                contentType.type !== "live" && contentType.platform !== "direct-playlist") {
                throw {
                    status: 69,
                    msg: "Subtitle downloads require a VOD or master playlist URL with available subtitles.",
                };
            }

            if (contentType.platform === "twitch" && (contentType.type === "live" || contentType.type === "vod") && !extension) {
                resetForm();
                return promptChromeExtensionModal()
            }

            if (contentType.platform === "rumble" && contentType.type === "playlist") {
                setPlaylistLoading(true);
                setPlaylistVideos(null);
                try {
                    const {videos} = await fetch(`/api/get-rumble-playlist?id=${encodeURIComponent(contentType.id || '')}`)
                        .then(r => r.json());
                    setPlaylistVideos(videos || []);
                } catch (err) {
                    logger.log("fetch rumble playlist error", err);
                    setPlaylistVideos([]);
                } finally {
                    setPlaylistLoading(false);
                }
                setState("default");
                return
            }

            let updatedUrl = contentType.platform === 'direct-playlist' && contentType.url ? contentType.url : url;

            updateMonitoringContext({
                contentType
            });

            video = await getVideoDetails({
                track,
                refresh,
                extension,
                url: updatedUrl,
                isHidden: queryParamPrivate,
                show,
                updateMonitoringContext
            })

            updateMonitoringContext({
                video
            });

            // if (video.downloadLink && contentType.platform === 'rumble') {
            //     const response = await fetch(video.downloadLink);
            //     const blob = await response.blob();
            //     const blobUrl = URL.createObjectURL(blob);
            //     const link = document.createElement("a");
            //     link.href = blobUrl;
            //     link.download = "";
            //     document.body.appendChild(link);
            //     link.click();
            //     document.body.removeChild(link);
            //     URL.revokeObjectURL(blobUrl);
            //
            //     setVideo({
            //         isLive: false,
            //         ...video,
            //     });
            //     setState("saved");
            //
            //     return showConfetti()
            // }

            if (video.downloadLink && enforceExtenstion === "vtt") {
                throw {
                    status: 69,
                    msg: "This video has no downloadable subtitle tracks. Use a VOD with available subtitles.",
                };
            }

            if (video.downloadLink) {

                if(video.platform === "rumble") {

                    if(chooseQuality) {
                        const {accepted, data: prefs} = await show({
                            component: RumbleChooseQualityPopup,
                            data: {size: "md", video, title: 'Choose format'},
                        })

                        if (!accepted) {
                            resetForm();
                            return;
                        }

                        setVideo({
                            isLive: false,
                            ...video,
                        });

                        if (prefs.ext === '.mp3') {
                            isSavingRumbleMp3 = true;
                            const filePromptResult = await writeFilePrompt(video.videoFileName, ".mp3", show);
                            if (!filePromptResult) {
                                track("Download Aborted");
                                return resetForm();
                            }
                            setDownloadSaveInfo(filePromptResult.saveInfo);
                            updateMonitoringContext({ext: ".mp3", save_location: filePromptResult.saveInfo.location});
                            track("Download Config", {...monitoringContext});
                            try {
                                setProgress('Downloading...');
                                const response = await fetch(video.audioUrl || video.downloadLink);

                                const total = Number(response.headers.get('content-length')) || 0;
                                let loaded = 0;
                                const reader = response.body.getReader();
                                const trackedStream = new ReadableStream<Uint8Array>({
                                    async pull(controller) {
                                        const {done, value} = await reader.read();
                                        if (done) {
                                            controller.close();
                                            return;
                                        }
                                        loaded += value.length;
                                        const loadedMb = (loaded / 1e6).toFixed(2);
                                        if (total) {
                                            const pct = +((loaded / total) * 100).toFixed(2);
                                            // setProgress(`Downloading ⸱ ${pct}% ⸱ ${loadedMb} / ${(total / 1e6).toFixed(1)} MB`);
                                            setProgress(pct);
                                        } else {
                                            setProgress(`Downloading ⸱ ${loadedMb} MB`);
                                        }
                                        controller.enqueue(value);
                                    }
                                });

                                const mp3Url = await toMp3(trackedStream, msg => {
                                    let ffmpegProgress = 0;
                                    try {
                                        ffmpegProgress = (((parseFfmpegOutput(msg) || 0.1) / video.duration) * 100).toFixed();
                                    } catch (err) {
                                    }
                                    setProgress(`Converting to audio ⸱ ${ffmpegProgress}% ⸱ ${msg}`)
                                });

                                await saveMp3Download(mp3Url, filePromptResult);
                            } catch (err) {
                                await discardMp3File(filePromptResult);
                                throw err;
                            }
                            setState("saved");

                            showConfetti()
                            return;
                        }else if (prefs.ext === '.aac') {
                            video.downloadLink = video.audioUrl;
                            video.videoFileName = video.videoFileName+".aac";
                            // return triggerDownload(video.audioUrl, video.videoFileName + ".aac");
                        }
                    }


                    // .mp4 falls through to the generic direct-download handling below.
                }


                window.open(video.downloadLink);
                setVideo({
                    isLive: false,
                    ...video,
                });
                setDownloadSaveInfo({location: "browser"});
                updateMonitoringContext({save_location: "browser"});
                setState("saved");
                if (video.type === 'clip' && video.platform === 'kick') {
                    track("kick clip as MP4", {clip_url: video.downloadLink, url: updatedUrl})
                }

                return showConfetti()
            }

            if (video.isPremium) {
                // enforce choose quality for premium
                chooseQuality = true;
            }

            logger.log({video})
        } catch (err) {
            logger.log("getVideoInfo error", err);
            setState("error");

            const urlFormatByPlatform = {
                kick: "kick.com/<channel>/videos/<video_id>",
                twitch: "twitch.tv/videos/<video_id>",
                rumble: "rumble.com/<video_id>",
            }
            let message = <><b>Video doesn't seem to exist.</b> Make sure video url has the following
                format: {urlFormatByPlatform[Platform] || urlFormatByPlatform.kick}</>

            if (err.status === 403) {
                const reopenUpsell = () => {
                    if (err.platform === 'twitch') {
                        showTwitchUpsellModal(show, {
                            source: 'err-message',
                            vodId: err.vodId,
                        });
                    } else {
                        showUpsellModal(show, {
                            isVariant1: isInNewUpsellExperiment(),
                            source: 'err-message',
                            vodId: err.vodId,
                            vodUrl: err.vodUrl
                        });
                    }
                };
                message = <>Video is <b>private</b> or <b>subscriber only</b>.{' '}
                    <a className="text-blue-700 cursor-pointer" onClick={(e) => {
                        e.preventDefault();
                        reopenUpsell();
                    }}>Download with credits</a>.
                </>


            }
            if (err.status === 404) {
                message = <>Video couldn't be <b>found</b>. Does it exist?</>
            }
            if (err.status === 429 || (err.status >= 500 && err.status < 600)) {
                message = <>This is a <b>temporary problem on our side</b>. Please{' '}
                    <a className="text-blue-700 cursor-pointer" onClick={(e) => {
                        e.preventDefault();
                        downloadClicked();
                    }}>try again</a>.
                </>
            }
            // if( err.message?.includes("is not valid url") ) {
            //     message = <>Your <b>URL</b> is invalid.</>
            // }

            if (err.status == 69) {
                message = err.msg || <>
                    <b>Opps.</b>
                </>

                if (err.upgradePlatform) {
                    const platformLabel = err.upgradePlatform === "twitch" ? "Twitch" : "Kick";
                    const openUpgradeOptions = () => {
                        if (err.upgradePlatform === "twitch") {
                            showTwitchUpsellModal(show, {
                                source: "license-platform-mismatch",
                                vodId: err.vodId,
                            });
                        } else {
                            showUpsellModal(show, {
                                isVariant1: isInNewUpsellExperiment(),
                                source: "license-platform-mismatch",
                                vodId: err.vodId,
                                vodUrl: err.vodUrl,
                            });
                        }
                    };

                    message = <>{err.msg}.{' '}
                        <a className="text-blue-700 cursor-pointer" onClick={(e) => {
                            e.preventDefault();
                            openUpgradeOptions();
                        }}>
                            View {platformLabel} upgrade options
                        </a>
                    </>;
                }
            }

            if (isSavingRumbleMp3) {
                message = saveErrorMessage(err) || "Ooops! Failed to download video... Try again, please.";
            }

            if (isArrayBufferAllocationError(err)) {
                message = MP3_OUT_OF_MEMORY_MESSAGE;
            }

            message = mp3ConversionMessage(err) || message;

            setError(
                message
            );
            const errMessage = errorMessageOf(err)
            if(errMessage !== ' is not valid url') {
                track("Error", {
                    ...monitoringContext,
                    url,
                    message: errMessage,
                    error_context: 'getVideoInfo',
                    ...mp3ErrorContext(err),
                });
            }
            return;
        }
        //=============== get playlist or direct file url to download
        //===================================================================================================================
        //===================================================================================================================
        //===================================================================================================================
        //===================================================================================================================
        //===================================================================================================================
        //===================================================================================================================
        //===================================================================================================================

        const downloadAndSaveLog = {
            state: 'before-downloading',
            writeStreamSuccess: false
        }
        let filePromptResult: FilePromptResult | null = null;
        try {
            downloadAndSaveLog.video = video;
            console.log({video})
            let downloadConfig = await getDownloadConfig({
                video,
                askForDownloadPreferences,
                platform: video.platform,
                type: video.type,
                videoPlaylist: video.videoPlaylist,
                twitchVodDetails: video.twitchVodDetails,
                chooseQuality
            }, enforceExtenstion, () => {
                const contentType = getPlatformAndContentTypeByUrl(video.url);

                track("VTT Recognized", {
                    ...monitoringContext,
                    channel: video.slug || contentType?.channel || null,
                    vod_url: video.url,
                    playlist_url: video.videoPlaylist || video.twitchVodDetails?.url || null,
                    uuid: video.uuid ?? null,
                });
            });

            if (!downloadConfig) {
                return resetForm();
            }
            console.log({downloadConfig})
            downloadAndSaveLog.getDownloadConfig = downloadConfig;

            //----------------------------------------------------------------------------------------------------------
            //----------------------------------------------------------------------------------------------------------
            //----------------------------------------------------------------------------------------------------------
            //----------------------------------------------------------------------------------------------------------
            //----------------------------------------------------------------------------------------------------------
            //----------------------------------------------------------------------------------------------------------

            console.log({
                video,
                downloadConfig
            })

            logger.log('video', {video})
            setVideo({
                isLive: downloadConfig.ongoingDownload,
                ...video
            });

            const suggestedName = isDev ? video.videoFileName + "-" + Date.now() : video.videoFileName;
            // await new Promise(resolve => setTimeout(resolve, 6000));
            filePromptResult = await writeFilePrompt(suggestedName, downloadConfig.ext, show)

            if (filePromptResult === null) {
                // likely aborted, so just return
                track("Download Aborted")
                return resetForm();
            }else {
                // track('writableStream captured')
            }


            const {writableStream, saveInfo} = filePromptResult;
            setDownloadSaveInfo(saveInfo);
            updateMonitoringContext({ext: downloadConfig.ext, save_location: saveInfo.location});
            track("Download Config", {...monitoringContext});

            setState("downloading");
            downloadAndSaveLog.state = "downloading";

            let newEmitter ;

            try {
                newEmitter = downloadPlaylistChunks({
                    log: downloadAndSaveLog,
                    isLive: downloadConfig.ongoingDownload,
                    fetchWitchExtension: video.fetchWithExtension,
                    uniqueSegmentsOnly: video.type === "clip",
                    trim: downloadConfig.trim,
                    variantPlaylistText: downloadConfig.variantPlaylistText,
                    variantPlaylistUrl: downloadConfig.variantPlaylistUrl,
                    writableStream,
                    outputExtension: downloadConfig.ext,
                    // mp3 is remuxed by ffmpeg during conversion anyway, so rewriting
                    // timestamps first would just be wasted work.
                    remux: downloadConfig.ext !== '.mp3' ? downloadConfig.remux : false,
                    updateMonitoringContext,
                    track,
                    setEstimated
                });
            }catch(err) {
                downloadAndSaveLog.downloadPlaylistChunksError = err?.message ?? 'error...'
                throw err;
            }
            // track('downloadPlaylistChunks started')
            setEmitter(newEmitter);
            newEmitter.on("segment-fetch-failed", (data) => {
                downloadAndSaveLog.segmentFetchFailed = data
                trackProcessSegmentFailed(data)
            })
            newEmitter.on("progress", progress => {
                downloadAndSaveLog.progress = progress;
                setProgress(progress)
            })

            await new Promise<void>((resolve, reject) => {
                newEmitter.on("success", async ({writableStream, segments}) => {
                    downloadAndSaveLog.writeStreamSuccess = true;

                    try {
                        if (downloadConfig.ext === '.mp3') {
                            setProgress('Converting to mp3...');
                            const totalSeconds = (segments ?? []).reduce((a, b) => a + b.duration, 0);
                            downloadAndSaveLog.mp3TotalSeconds = totalSeconds;

                            const mp3Url = await toMp3(writableStream, msg => {
                                let ffmpegProgress = 0;
                                try {
                                    ffmpegProgress = (((parseFfmpegOutput(msg) || 0.1) / totalSeconds) * 100).toFixed();
                                } catch (err) {
                                }
                                setProgress(`Converting to mp3 ⸱ ${ffmpegProgress}% ⸱ ${msg}`)
                            });

                            await saveMp3Download(mp3Url, filePromptResult);
                        }

                        setState("saved");
                        downloadAndSaveLog.state = "saved"
                        showConfetti();
                        resolve();
                    } catch (err) {
                        downloadAndSaveLog.mp3SaveErrThrown = true
                        downloadAndSaveLog.mp3SaveErr = err
                        reject(err);
                    }
                })
                newEmitter.on("error", (err) => {
                    downloadAndSaveLog.writeStreamError = err;
                    downloadAndSaveLog.writeStreamErrorType = typeof err;
                    downloadAndSaveLog.writeStreamErrorMessage = err?.message ?? 'unnamed-error';
                    reject(err)
                })
            })
        } catch (err) {
            logger.log("downloadAndSave error", err);
            setState("error");
            if (filePromptResult) {
                await discardMp3File(filePromptResult);
            }
            const errText = errorMessageOf(err)
            let errMessage: React.ReactNode = enforceExtenstion === "vtt"
                ? errText
                : "Ooops! Failed to download video... Try again, please.";

            if (isMissingDownloadDestinationError(err)) {
                errMessage = MISSING_DESTINATION_ERROR_MESSAGE
            }

            if (isWriteAbortedError(err)) {
                errMessage = WRITE_ABORTED_ERROR_MESSAGE
            }

            if (isArrayBufferAllocationError(err)) {
                errMessage = MP3_OUT_OF_MEMORY_MESSAGE
            }

            errMessage = mp3ConversionMessage(err) || errMessage;
            if (isPlaylistFetchError(err)) {
                errMessage = <>
                    <b>We could not load this video's stream.</b>
                    <p>
                        The site did not return a playable video playlist, so the download could not start. This is
                        usually a temporary problem on the video host's side. Wait a moment, then start the download
                        again.
                    </p>
                </>
            }

            if (isStreamSaverError(err)) {
                errMessage = <>
                    <b>Your browser could not start the file download.</b>
                    <p>
                        This browser cannot save the video file directly. Click "Customize (Format, Quality, Trim)",
                        choose audio (.mp3), and download again — that path works on every browser. To keep the video,
                        open this page in Chrome on a computer.
                    </p>
                </>
            }

            if (errText.includes(QUOTA_EXCEEDED_ERROR_TEXT)) {
                errMessage = QUOTA_EXCEEDED_ERROR_MESSAGE
            }

            if (errText.includes(SAVE_PERMISSION_ERROR_TEXT)) {
                errMessage = SAVE_PERMISSION_ERROR_MESSAGE
            }
            setError(errMessage);

            try {
                new URL(url);
                downloadAndSaveLog.finalError = errText
                window.posthog?.logger.error("downloadAndSave:err", downloadAndSaveLog);
                track("Error", {
                    ...monitoringContext,
                    message: errText,
                    error_context: 'downloadAndSave',
                    ...mp3ErrorContext(err),
                });
            } catch (trackErr) {

            }
        }
        // downloadClicked ==========================================================================================
    }

    const buyMeCoffeeButton = useMemo(() => <DonateWithPaypal track={track}/>, [])
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            downloadClicked()
        }
    };

    const inputRef = useRef<HTMLInputElement>(null);

// async function dodgeCloudfront(apiUrl) {
//
//     throw {
//         status: 69,
//         message: "403...",
//         msg: <>We can't get video details, because it's antibot protected.
//             <br/>
//             <br/>
//             Please, open <a
//                 href={'https://kick.com'}
//                 target={"_blank"} className={"text-blue-700 underline cursor-pointer"}>kick.com</a>, and <b>verify
//                 you're not a bot</b>. Then try again.
//         </>
//     }
//
//     const extensionVersion = getExtensionVersion(extension);
//     const isVersionLegit = !!extension && extensionVersion > 13;
//     track("[Dodge Cloudfront] started", {extensionVersion, isVersionLegit})
//     if (!extension || !isVersionLegit) {
//         track('[Dodge Cloudfront] Showing modal')
//         show({
//             component: ChromeExtensionCloudflareDodgePopup,
//             data: {
//                 isVersionLegit,
//                 extensionInstalled: !!extension
//             }
//         })
//         throw {
//             status: 69,
//             message: "To download this content, please, install the extension",
//             msg: <>Video details couldn't be loaded. To download this content, please, install <a
//                 href={CHROME_EXTENSION_URL}
//                 target={"_blank"} className={"text-blue-700 underline cursor-pointer"}>our browser
//                 extension</a>.</>
//         }
//     }
//     track("[Dodge Cloudfront] pulling data", {apiUrl})
//     const details = (await window.sendMessage('dodge-cloudfront', {
//         url: apiUrl
//     }));
//     track("[Dodge Cloudfront] result", details)
//     if (details.error) {
//         if (details.error === 'tab closed') {
//             throw {
//                 status: 69,
//                 message: `details.error: ${details.error}`,
//                 msg: <>Opps, The kick.com tab was closed... Try again, please.</>
//             }
//         }
//         if (details.error === 'timeout...') {
//             throw {
//                 status: 69,
//                 message: `details.error: ${details.error}`,
//                 msg: <>Couldn't load kick.com in a new tab. Try again, please</>
//             }
//         }
//     }
//     return details.data;
// }


    return (
        <div className={"flex justify-center flex-col items-center"}>
            <>
                {
                    false && (<>
                        {TEST_VIDEOS.map(([url, desc], i) => (
                            <p className={"text-sm"} onClick={() => setUrl(url)} key={i}>{url} <span
                                className={"text-xl font-bold text-amber-300"}>({desc})</span> <a
                                href={`/test?download=${url}`}>query</a></p>))}
                    </>)
                }
            </>

            {/*<div className={`items-center flex gap-2`}>*/}
            {/*    <Chip color="primary" variant="solid">*/}
            {/*    <span className={`font-bold`}>~37 TB</span> of VODs downloaded last month 🎉*/}
            {/*</Chip>*/}
            {/*    /!*<p className={`text-sm`}> of VODs downloaded so far...</p>*!/*/}
            {/*</div>*/}
            <div className="flex flex-col gap-4 w-full pt-2 pb-4" style={{maxWidth: '600px'}}>
                <div className={"-mb-[6px] flex items-center justify-center min-h-8"}>
                    {hasPremium && <PremiumManager/>}
                </div>
                <Input ref={inputRef} autoFocus
                       isClearable
                       isDisabled={state !== "default" && state !== "saved" && state !== "error"}
                       label={enforceExtenstion === "vtt" ? "VOD URL" : "VOD or clip URL"}
                       onKeyDown={handleKeyDown}
                       onChange={() => {
                           if (state === "error") {
                               setState("default")
                               setError("")
                           }
                       }}
                       value={url} onValueChange={setUrl} type="text" variant="bordered" isRequired size={"lg"}
                       alt={enforceExtenstion === "vtt" ? "VOD URL" : "VOD or clip URL"}
                       placeholder={
                           {
                               kick: "https://kick.com/adinross/videos/6ba780fe-...",
                               twitch: "https://www.twitch.tv/videos/1876537211",
                               rumble: "https://rumble.com/v1abc123-example.html",
                           }[Platform] || "https://kick.com/adinross/videos/6ba780fe-..."
                       }/>
                <div className={"flex gap-4 flex-col md:flex-row"}>
                    <Button className="flex-grow"
                            isDisabled={(state !== "default" && state !== "saved" && state !== "error")}
                            onPress={() => downloadClicked()}
                            startContent={<DownloadIcon/>}
                            style={{
                                kick: {},
                                twitch: {background: '#772ce8', color: 'white'},
                                rumble: {background: '#84c742', color: 'white'},
                            }[Platform] || {}}
                            color={"primary"}>{enforceExtenstion === "vtt" ? "Download subtitles (.vtt)" : "Download (Best Quality)"}</Button>

                    <Button className="flex-grow"
                            isDisabled={(state !== "default" && state !== "saved" && state !== "error")}
                            onPress={() => downloadClicked(true)}
                            startContent={<QualityIcon width={"20px"} height={"20px"}/>}
                            color={"default"}>{enforceExtenstion === "vtt" ? "Customize (Format, Language, Trim)" : Platform === "rumble" ? "Customize (Format)" : "Customize (Format, Quality, Trim)"}</Button>
                </div>

                {playlistLoading && (
                    <p className={"text-sm text-center text-gray-500"}>Loading playlist…</p>
                )}

                {playlistVideos && playlistVideos.length > 0 && (
                    <div className={"flex flex-col gap-2"}>
                        {playlistVideos.map((item) => {
                            const itemKey = item.id || item.url || '';
                            const isDownloaded = clickedPlaylistItems.has(itemKey);
                            return (
                            <button
                                key={itemKey}
                                type="button"
                                onClick={() => {
                                    if (item.url) {
                                        window.open(`?download=${encodeURIComponent(item.url)}`, '_blank');
                                        setClickedPlaylistItems(prev => new Set(prev).add(itemKey));
                                    }
                                }}
                                className={`group flex items-center gap-3 p-2 rounded-xl border text-left cursor-pointer ${
                                    isDownloaded
                                        ? "border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950 hover:bg-green-100 dark:hover:bg-green-900"
                                        : "border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
                                }`}
                            >
                                {item.thumbnail && (
                                    <img src={item.thumbnail} alt={item.title || ''}
                                         className={"w-28 aspect-video object-cover rounded-md flex-shrink-0"}
                                         loading="lazy"/>
                                )}
                                <div className={"min-w-0 flex-grow"}>
                                    <p className={"text-sm font-semibold line-clamp-2 text-gray-900 dark:text-gray-100"}>{item.title}</p>
                                    <p className={"text-xs text-gray-600 dark:text-gray-400"}>
                                        {item.channel}{item.duration ? ` · ${item.duration}` : ''}
                                    </p>
                                </div>
                                <DownloadIcon
                                    className={"flex-shrink-0 mr-1 opacity-0 group-hover:opacity-100 transition-opacity text-gray-700 dark:text-gray-200"}/>
                            </button>
                            );
                        })}
                    </div>
                )}

                {playlistVideos && playlistVideos.length === 0 && !playlistLoading && (
                    <p className={"text-sm text-center text-gray-500"}>No videos found in this playlist.</p>
                )}

                {state === "error" ? <>
                    <>
                        <div
                            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl relative flex items-center gap-2"
                            role="alert">
                            <strong className="font-bold ">
                                <BaselineError width={"30px"} height={"30px"}/>
                            </strong>
                            <span className="block sm:inline">{error}</span>
                        </div>
                    </>
                </> : null}

                {/*{isDev && (<SubOnlyContentAvailable channelSlug={'pluggedrp'} />)}*/}

                {state === 'downloading' || state === "preparing" ?
                    <ProgressBar progress={progress} video={video} estimated={estimated} emitter={emitter}
                                 buyMeCoffeeButton={buyMeCoffeeButton}/> : null}

                {state === "saved" ? (
                    <div className={"pb-8"}>
                        {downloadSaveInfo && (
                            <DownloadSavedGuide
                                emoji={EMOJI}
                                saveInfo={downloadSaveInfo}
                                userAgent={userAgent}
                            />
                        )}
                        <div className={"flex justify-center pb-8"}>
                            {buyMeCoffeeButton}
                        </div>
                    </div>
                ) : null}

                {/*{state === "saved" && video?.platform === "kick" && video.slug ? (*/}
                {/*    <HorizontalChannelVideos currentVideoUrl={video.url} slug={video.slug} />*/}
                {/*) : null}*/}

                {video && state !== 'error' && state !== 'saved' && (
                    <div>
                        <p className={`mb-2 text-neutral-300 ${video.videoThumbnail ? 'text-left truncate w-[600px]' : 'text-center'}`}>
                            {video.videoName}
                        </p>
                        {video.videoThumbnail &&
                            <img className={"shadow-2xl"} width="600px" src={video.videoThumbnail}/>}
                    </div>
                )}
            </div>
        </div>
    );
}


function updateMonitoringContext(context = {}) {
    monitoringContext = ({
        ...monitoringContext,
        ...context
    })
}

function resetMonitoringContext() {
    monitoringContext = Object.fromEntries(
        Object.entries(monitoringContext).filter(([key]) => PERSISTENT_MONITORING_KEYS.includes(key))
    );
}
